import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// Agenda flow state (E1), post-checkout. Persisted to LOCALSTORAGE (not
// sessionStorage) so an 8h SYW hold and the post-reserve code survive closing
// the tab — the customer is bounced to a Shopify checkout tab and may return in
// a fresh one. sessionStorage would silently discard a hold the customer paid
// for. Matches the wizardStore direction (P0-6). Kept separate from the wizard
// store — different lifecycle.

export type AgendaStage = 'terms' | 'calendar' | 'verifying';

interface AgendaState {
  // Payment/order reference proving checkout completed (from ?ref= or checkout).
  paymentRef: string | null;
  // Snapshot of installTier taken when the agenda flow begins, so later wizard
  // edits (the cart tier buttons mutate the shared wizardStore) can't retrobook
  // or hide a confirmed agenda after the fact.
  tierSnapshot: string | null;
  termsAccepted: boolean;
  termsVersion: string | null;
  selectedDate: string | null; // YYYY-MM-DD
  // SYW hold code captured the instant reserve() succeeds, BEFORE confirm(). If
  // confirm then fails (blip / tab close), the hold isn't orphaned: the retry
  // resumes confirm on this code instead of re-reserving (which would 409 on the
  // customer's own hold). Cleared once the booking is confirmed.
  reservedCode: string | null;
  interventionCode: string | null; // SYW code after reserve+confirm
  status: string | null; // SYW status (AGUARDA_VERIFICACAO after confirm)
  stage: AgendaStage;

  setPaymentRef: (ref: string | null) => void;
  setTierSnapshot: (tier: string | null) => void;
  acceptTerms: (version: string | null) => void;
  setSelectedDate: (date: string | null) => void;
  setReserved: (code: string | null) => void;
  setBooked: (code: string, status: string, date: string) => void;
  setStage: (stage: AgendaStage) => void;
  reset: () => void;
}

const initial = {
  paymentRef: null as string | null,
  tierSnapshot: null as string | null,
  termsAccepted: false,
  termsVersion: null as string | null,
  selectedDate: null as string | null,
  reservedCode: null as string | null,
  interventionCode: null as string | null,
  status: null as string | null,
  stage: 'terms' as AgendaStage,
};

export const useAgendaStore = create<AgendaState>()(
  persist(
    (set) => ({
      ...initial,
      setPaymentRef: (ref) => set({ paymentRef: ref }),
      setTierSnapshot: (tier) => set({ tierSnapshot: tier }),
      acceptTerms: (version) => set({ termsAccepted: true, termsVersion: version, stage: 'calendar' }),
      setSelectedDate: (date) => set({ selectedDate: date }),
      setReserved: (code) => set({ reservedCode: code }),
      setBooked: (code, status, date) =>
        set({ interventionCode: code, status, selectedDate: date, reservedCode: null, stage: 'verifying' }),
      setStage: (stage) => set({ stage }),
      reset: () => set({ ...initial }),
    }),
    {
      name: 'syhouse:agenda',
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
